import { useCallback, useEffect, useRef, useState } from "react";

// A dual-thumb range slider that never clamps thumbs against each other:
// low and high move independently. If low > high, we simply swap on read.
// The visual track shows the [min(low,high), max(low,high)] window.
export const DualRangeSlider = ({
  min = 0,
  max = 255,
  low,
  high,
  onChange,
  testId,
}) => {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null); // "low" | "high" | null
  const [localLow, setLocalLow] = useState(low);
  const [localHigh, setLocalHigh] = useState(high);

  useEffect(() => {
    if (dragging === null) {
      setLocalLow(low);
      setLocalHigh(high);
    }
  }, [low, high, dragging]);

  const pctLow = ((localLow - min) / (max - min)) * 100;
  const pctHigh = ((localHigh - min) / (max - min)) * 100;
  const winStart = Math.min(pctLow, pctHigh);
  const winEnd = Math.max(pctLow, pctHigh);

  const posFromEvent = useCallback(
    (clientX) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return 0;
      const raw = (clientX - rect.left) / rect.width;
      const clamped = Math.max(0, Math.min(1, raw));
      return Math.round(min + clamped * (max - min));
    },
    [min, max],
  );

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const val = posFromEvent(clientX);
      if (dragging === "low") setLocalLow(val);
      else setLocalHigh(val);
    };
    const up = () => {
      setDragging(null);
      // Commit to parent
      onChange(
        dragging === "low" ? localLowRef.current : localLow,
        dragging === "high" ? localHighRef.current : localHigh,
      );
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  // Keep refs of the latest local values so mouseup callback captures them.
  const localLowRef = useRef(localLow);
  const localHighRef = useRef(localHigh);
  useEffect(() => {
    localLowRef.current = localLow;
    localHighRef.current = localHigh;
  }, [localLow, localHigh]);

  // Continuously commit while dragging so canvas updates live.
  useEffect(() => {
    if (dragging) onChange(localLow, localHigh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLow, localHigh]);

  const startDrag = (which) => (e) => {
    e.preventDefault();
    setDragging(which);
  };

  return (
    <div
      ref={trackRef}
      data-testid={testId}
      className="relative h-6 flex items-center select-none touch-none"
    >
      <div className="absolute left-0 right-0 h-1.5 rounded-full bg-zinc-800" />
      <div
        className="absolute h-1.5 rounded-full brand-gradient"
        style={{ left: `${winStart}%`, width: `${winEnd - winStart}%` }}
      />
      <button
        type="button"
        aria-label="Min"
        data-slider-thumb="true"
        onMouseDown={startDrag("low")}
        onTouchStart={startDrag("low")}
        className="absolute w-4 h-4 rounded-full brand-gradient border-2 border-white/90 shadow-md shadow-rose-500/25 -translate-x-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
        style={{ left: `${pctLow}%` }}
      />
      <button
        type="button"
        aria-label="Max"
        data-slider-thumb="true"
        onMouseDown={startDrag("high")}
        onTouchStart={startDrag("high")}
        className="absolute w-4 h-4 rounded-full brand-gradient border-2 border-white/90 shadow-md shadow-rose-500/25 -translate-x-1/2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
        style={{ left: `${pctHigh}%` }}
      />
    </div>
  );
};
